import 'maplibre-gl/dist/maplibre-gl.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import {
  AttributionControl,
  LngLatBounds,
  Map as MapLibreMap,
  NavigationControl,
  type GeoJSONSourceSpecification,
} from 'maplibre-gl';
import { CircleAlert, Crosshair, Loader2, LogOut, MapPinned, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  acceptPositionCorrection,
  fetchPositionCorrection,
  fetchSiteAreas,
  releaseDevice,
  request,
} from '../../api/client';
import { usePrefs } from '../../app/prefs';
import { Modal } from '../../ui/components';
import { formatDateTime, formatNumber } from '../../ui/format';
import { ErrorNote } from '../admin/ErrorNote';
import { useMe } from '../auth/session';
import { baseStyle, cssVar } from '../map/baseStyle';
import { decidesPlacement } from '../sites/PlacementReview';
import './positionCorrection.css';

/**
 * GPS position correction and device release (user decision U19, 2026-10-21; superadmin only). A position is never
 * typed or dragged: it comes from two "Check GPS" readings of the device itself, at least 5 minutes apart and within
 * 15 m of each other. The server decides what accepting them does (it re-checks everything from the stored readings):
 * same zone → accept; another zone of the same client → accept, moved there; another client's area or outside every
 * area → release only (back to Discovered, mandatory reason and acknowledgement; auto-adoption then places it).
 */

type Correction = api.PositionCorrection;

export const correctionKey = (id: string) => ['position-correction', id] as const;

/** GPS reads, correction and release are the superadmin's only. */
export const useGpsFixAllowed = () => decidesPlacement(useMe().data);

const useCorrection = (id: string, enabled: boolean) =>
  useQuery({ queryKey: correctionKey(id), queryFn: () => fetchPositionCorrection(id), enabled });

/** A reading is far from the known position (or the device has none): something to correct. */
const actionable = (c: Correction | undefined) =>
  Boolean(c && c.state !== 'none' && c.state !== 'within_threshold');

/** "Correct position…": shown only when the latest GPS readings differ from the known position. */
export function CorrectPositionButton({ id, name }: { id: string; name: string }) {
  const { t } = useTranslation();
  const allowed = useGpsFixAllowed();
  const q = useCorrection(id, allowed);
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  if (!allowed || !actionable(q.data)) return null;
  return (
    <>
      <button ref={opener} type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
        <Crosshair size={15} /> {t('devices.gpsFix.correct')}
      </button>
      {open && (
        <PositionCorrectionDialog id={id} name={name} onClose={() => setOpen(false)} returnFocus={opener} />
      )}
    </>
  );
}

/** "Release device…" on the device page (superadmin): back to Discovered, with a reason. */
export function ReleaseButton({
  id,
  name,
  siteName,
  onReleased,
}: {
  id: string;
  name: string;
  siteName: string;
  onReleased: () => void;
}) {
  const { t } = useTranslation();
  const allowed = useGpsFixAllowed();
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  if (!allowed) return null;
  return (
    <>
      <button ref={opener} type="button" className="btn btn-ghost" onClick={() => setOpen(true)}>
        <LogOut size={15} /> {t('devices.gpsFix.release')}
      </button>
      {open && (
        <Modal
          title={t('devices.gpsFix.releaseTitle')}
          subtitle={name}
          size="small"
          onClose={() => setOpen(false)}
          returnFocus={opener}
        >
          <ReleaseForm
            id={id}
            name={name}
            siteName={siteName}
            onCancel={() => setOpen(false)}
            onReleased={onReleased}
          />
        </Modal>
      )}
    </>
  );
}

function PositionCorrectionDialog({
  id,
  name,
  onClose,
  returnFocus,
}: {
  id: string;
  name: string;
  onClose: () => void;
  returnFocus: React.RefObject<HTMLButtonElement | null>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useCorrection(id, true);
  const [releasing, setReleasing] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: correctionKey(id) });
  const check = useMutation({
    mutationFn: () =>
      request(
        'POST',
        `/api/v1/devices/${id}/queries`,
        { query_key: 'location.read' },
        (v) => v as api.DeviceQueryResponse,
      ),
    onSettled: () => refresh(),
  });
  const accept = useMutation({
    mutationFn: (checkId: string) => acceptPositionCorrection(id, checkId),
    onSuccess: async (r) => {
      setDone(
        r.moved
          ? t('devices.gpsFix.acceptedMoved', { site: sitePath(c?.placement?.target ?? null) })
          : t('devices.gpsFix.accepted'),
      );
      // this device only: its detail and its correction (the marker moves with the realtime registry event)
      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['device', id] })]);
    },
    onError: () => refresh(),
  });
  const c = q.data;
  const placement = c?.state === 'ready' ? c.placement : null;
  const releaseOnly = placement?.kind === 'other_client' || placement?.kind === 'outside_areas';
  const busy = check.isPending || accept.isPending;
  const checkFailed =
    check.data && !check.data.ok
      ? t(`devices.query.error.${check.data.error_code ?? 'default'}` as 'devices.query.error.default', {
          defaultValue: t('devices.query.error.default', { code: check.data.error_code }),
        })
      : null;
  const nextAt = c?.next_check_after ? new Date(c.next_check_after) : null;
  const canCheck = !nextAt || nextAt.getTime() <= Date.now();

  return (
    <Modal
      title={t('devices.gpsFix.title')}
      subtitle={name}
      onClose={onClose}
      returnFocus={returnFocus}
      footer={
        !releasing && (
          <div className="toolbar" style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {c && c.state !== 'ready' && (
              <button
                type="button"
                className="btn"
                disabled={busy || !canCheck}
                onClick={() => check.mutate()}
              >
                {check.isPending ? <Loader2 size={14} className="spin" /> : <MapPinned size={14} />}{' '}
                {t('devices.gpsFix.checkAgain')}
              </button>
            )}
            {c?.state === 'ready' && c.pair && !releaseOnly && !done && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => accept.mutate(c.pair![0])}
              >
                {t('devices.gpsFix.accept')}
              </button>
            )}
            {c?.state === 'ready' && releaseOnly && (
              <button type="button" className="btn btn-danger" onClick={() => setReleasing(true)}>
                <LogOut size={15} /> {t('devices.gpsFix.release')}
              </button>
            )}
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t(done ? 'common.close' : 'common.cancel')}
            </button>
          </div>
        )
      }
    >
      <div className="gpsfix">
        {q.isPending && <span className="muted">{t('common.loading')}</span>}
        <ErrorNote error={q.error ?? accept.error ?? check.error} />
        {checkFailed && (
          <div className="form-error" role="alert">
            {checkFailed}
          </div>
        )}
        {done && (
          <div className="notice" role="status">
            {done}
          </div>
        )}
        {c && <CorrectionMap correction={c} />}
        {c && !done && <CorrectionState correction={c} />}
        {c && placement && !done && <PlacementNote correction={c} />}
        {c && releasing && c.state === 'ready' && (
          <ReleaseForm
            id={id}
            name={name}
            siteName={sitePath(c.site)}
            checkId={c.pair?.[0]}
            onCancel={() => setReleasing(false)}
            onReleased={onClose}
          />
        )}
        {c && c.checks.length > 0 && (
          <details className="small">
            <summary>{t('devices.gpsFix.readings', { count: c.checks.length })}</summary>
            <ul className="gpsfix-readings mono">
              {c.checks.map((r) => (
                <li key={r.id}>
                  {formatDateTime(r.checked_at)} · {formatNumber(r.lat, 6)}, {formatNumber(r.lon, 6)}
                  {r.distance_m !== null &&
                    ` · ${t('devices.gpsFix.metres', { m: formatNumber(r.distance_m, 0) })}`}
                  {c.pair?.includes(r.id) && ` · ${t('devices.gpsFix.used')}`}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Modal>
  );
}

const sitePath = (s: { path: string[] } | null | undefined) => (s?.path.length ? s.path.join(' › ') : '—');

function CorrectionState({ correction: c }: { correction: Correction }) {
  const { t } = useTranslation();
  const m = (v: number | null | undefined) => formatNumber(v ?? 0, 0);
  if (c.state === 'ready' && c.proposed)
    return (
      <p className="gpsfix-summary">
        {t(c.current ? 'devices.gpsFix.proposed' : 'devices.gpsFix.proposedNoCurrent', {
          lat: formatNumber(c.proposed.lat, 6),
          lon: formatNumber(c.proposed.lon, 6),
          m: m(c.proposed.distance_m),
          spread: m(c.spread_m),
        })}
      </p>
    );
  return (
    <div className="notice" role="status">
      {t(`devices.gpsFix.state.${c.state}` as 'devices.gpsFix.state.none', {
        m: c.state === 'inconsistent' ? m(c.spread_m) : m(c.threshold_m),
        time: c.next_check_after ? formatDateTime(c.next_check_after) : '',
      })}
    </div>
  );
}

function PlacementNote({ correction: c }: { correction: Correction }) {
  const { t } = useTranslation();
  const p = c.placement!;
  const text = t(`devices.gpsFix.place.${p.kind}` as 'devices.gpsFix.place.same_zone', {
    from: sitePath(c.site),
    to: sitePath(p.target),
  });
  if (p.kind === 'same_zone') return <p className="gpsfix-summary">{text}</p>;
  const danger = p.kind !== 'other_zone';
  return (
    <div className={`gpsfix-alert ${danger ? 'danger' : 'warning'}`} role="alert">
      {danger ? <CircleAlert size={18} /> : <TriangleAlert size={18} />}
      <span>{text}</span>
    </div>
  );
}

/**
 * Release: a reason (≥ 10 characters) and an explicit acknowledgement, both required by the server too. The device
 * goes back to Discovered; auto-adoption may place it at another client (user's choice, 2026-10-21).
 */
function ReleaseForm({
  id,
  name,
  siteName,
  checkId,
  onCancel,
  onReleased,
}: {
  id: string;
  name: string;
  siteName: string;
  checkId?: string | undefined;
  onCancel: () => void;
  onReleased: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [ack, setAck] = useState(false);
  const [result, setResult] = useState<api.DeviceReleased | null>(null);
  const release = useMutation({
    mutationFn: () =>
      releaseDevice(id, {
        reason: reason.trim(),
        acknowledged: true,
        ...(checkId ? { check_id: checkId } : {}),
      }),
    onSuccess: setResult,
  });
  if (result)
    return (
      <div className="gpsfix-release">
        <div className="notice" role="status">
          {t(result.locating ? 'devices.gpsFix.releasedLocating' : 'devices.gpsFix.released', { name })}
        </div>
        <div className="toolbar" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={onReleased}>
            {t('common.close')}
          </button>
        </div>
      </div>
    );
  const valid = reason.trim().length >= 10 && ack;
  return (
    <form
      className="gpsfix-release"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) release.mutate();
      }}
    >
      <div className="gpsfix-alert danger">
        <CircleAlert size={18} />
        <span>{t('devices.gpsFix.releaseIntro', { name, site: siteName })}</span>
      </div>
      <label className="field">
        <span>{t('devices.gpsFix.reason')}</span>
        <textarea
          className="input"
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
      </label>
      <label className="toolbar small" style={{ alignItems: 'flex-start' }}>
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
        <span>{t('devices.gpsFix.ack', { site: siteName })}</span>
      </label>
      <ErrorNote error={release.error} />
      <div className="toolbar" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn-danger-solid" disabled={!valid || release.isPending}>
          {release.isPending && <Loader2 size={14} className="spin" />} {t('devices.gpsFix.releaseConfirm')}
        </button>
      </div>
    </form>
  );
}

/** Current position, the readings, the proposed position and the areas of the clients involved. */
function CorrectionMap({ correction: c }: { correction: Correction }) {
  const { t } = useTranslation();
  const theme = usePrefs((s) => s.theme);
  const ref = useRef<HTMLDivElement>(null);
  const areas = useQuery({ queryKey: ['admin', 'site-areas'], queryFn: fetchSiteAreas, staleTime: 60_000 });
  const target = c.state === 'ready' ? c.placement?.target : null;
  const crossClient = Boolean(target && c.site && target.client_id !== c.site.client_id);
  useEffect(() => {
    if (!ref.current) return;
    const pts: [number, number][] = [
      ...(c.current ? [[c.current.lon, c.current.lat] as [number, number]] : []),
      ...c.checks.map((r) => [r.lon, r.lat] as [number, number]),
    ];
    if (!pts.length) return;
    const map = new MapLibreMap({
      container: ref.current,
      style: baseStyle(theme),
      center: pts[0]!,
      zoom: 17,
      attributionControl: false,
    });
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new AttributionControl({ compact: true }), 'bottom-right');
    const b = new LngLatBounds(pts[0]!, pts[0]!);
    for (const p of pts) b.extend(p);
    map.fitBounds(b, { padding: 60, duration: 0, maxZoom: 18 });
    map.once('style.load', () => {
      const own = new Set([c.site?.client_id, c.site?.site_id].filter(Boolean));
      const other = new Set([target?.client_id, target?.site_id].filter(Boolean));
      const feats = (areas.data?.features ?? []).filter((f) => own.has(f.id) || other.has(f.id));
      map.addSource('areas', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: feats.map((f) => ({
            type: 'Feature',
            properties: { other: other.has(f.id) && !own.has(f.id) && crossClient },
            geometry: f.geometry,
          })),
        },
      });
      map.addLayer({
        id: 'areas-line',
        type: 'line',
        source: 'areas',
        paint: {
          'line-color': ['case', ['get', 'other'], cssVar('--color-critical'), cssVar('--color-text-muted')],
          'line-width': 2,
          'line-dasharray': [3, 2],
        },
      });
      const features: {
        type: 'Feature';
        properties: { kind: string };
        geometry: { type: 'Point' | 'LineString'; coordinates: number[] | number[][] };
      }[] = [];
      if (c.current && c.proposed)
        features.push({
          type: 'Feature',
          properties: { kind: 'link' },
          geometry: {
            type: 'LineString',
            coordinates: [
              [c.current.lon, c.current.lat],
              [c.proposed.lon, c.proposed.lat],
            ],
          },
        });
      if (c.current)
        features.push({
          type: 'Feature',
          properties: { kind: 'current' },
          geometry: { type: 'Point', coordinates: [c.current.lon, c.current.lat] },
        });
      for (const r of c.checks)
        features.push({
          type: 'Feature',
          properties: { kind: 'reading' },
          geometry: { type: 'Point', coordinates: [r.lon, r.lat] },
        });
      if (c.proposed)
        features.push({
          type: 'Feature',
          properties: { kind: 'proposed' },
          geometry: { type: 'Point', coordinates: [c.proposed.lon, c.proposed.lat] },
        });
      map.addSource('fix', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features } as unknown as GeoJSONSourceSpecification['data'],
      });
      map.addLayer({
        id: 'fix-link',
        type: 'line',
        source: 'fix',
        filter: ['==', ['get', 'kind'], 'link'],
        paint: { 'line-color': cssVar('--color-accent'), 'line-width': 2, 'line-dasharray': [1, 1.5] },
      });
      map.addLayer({
        id: 'fix-points',
        type: 'circle',
        source: 'fix',
        filter: ['!=', ['get', 'kind'], 'link'],
        paint: {
          'circle-radius': ['match', ['get', 'kind'], 'reading', 4, 8],
          'circle-color': [
            'match',
            ['get', 'kind'],
            'current',
            cssVar('--color-surface'),
            'reading',
            cssVar('--color-accent'),
            cssVar('--color-accent'),
          ],
          'circle-stroke-color': [
            'match',
            ['get', 'kind'],
            'current',
            cssVar('--color-text-muted'),
            cssVar('--color-surface'),
          ],
          'circle-stroke-width': ['match', ['get', 'kind'], 'reading', 1, 3],
          'circle-opacity': ['match', ['get', 'kind'], 'reading', 0.7, 1],
        },
      });
    });
    return () => map.remove();
    // rebuilt when the readings, the proposal or the areas change (a dialog-local map, never the main map)
  }, [c, theme, areas.data, target, crossClient]);
  if (!c.checks.length && !c.current) return null;
  return (
    <div className="gpsfix-map-wrap">
      <div ref={ref} className="gpsfix-map" aria-label={t('devices.gpsFix.mapLabel')} role="img" />
      <div className="gpsfix-legend small">
        {c.current && (
          <span>
            <i className="dot current" /> {t('devices.gpsFix.legend.current')}
          </span>
        )}
        <span>
          <i className="dot reading" /> {t('devices.gpsFix.legend.reading')}
        </span>
        {c.proposed && (
          <span>
            <i className="dot proposed" /> {t('devices.gpsFix.legend.proposed')}
          </span>
        )}
      </div>
    </div>
  );
}
